import getStroke from 'perfect-freehand';

import type { DoodleStroke } from './use-doodle';

const STROKE_OPTIONS_BASE = {
	thinning: 0.6,
	smoothing: 0.5,
	streamline: 0.5,
	easing: (t: number) => Math.sin((t * Math.PI) / 2),
	last: true,
};

function average(a: number, b: number): number {
	return (a + b) / 2;
}

export function getSvgPathFromStroke(points: number[][]): string {
	const len = points.length;
	if (len < 4) return '';

	let a = points[0];
	let b = points[1];
	const c = points[2];

	let result = `M${a[0].toFixed(2)},${a[1].toFixed(2)} Q${b[0].toFixed(2)},${b[1].toFixed(2)} ${average(b[0], c[0]).toFixed(2)},${average(b[1], c[1]).toFixed(2)} T`;

	for (let i = 2, max = len - 1; i < max; i++) {
		a = points[i];
		b = points[i + 1];
		result += `${average(a[0], b[0]).toFixed(2)},${average(a[1], b[1]).toFixed(2)} `;
	}

	result += 'Z';
	return result;
}

export function getStrokeOutline(stroke: DoodleStroke): number[][] {
	const simulatePressure = !stroke.points.some((p) => p.length >= 3 && p[2] !== 0.5);

	return getStroke(stroke.points, {
		...STROKE_OPTIONS_BASE,
		size: stroke.size * 4.25,
		simulatePressure,
	});
}

export function renderStrokeToCtx(ctx: CanvasRenderingContext2D, stroke: DoodleStroke): void {
	if (stroke.tool === 'fill') {
		floodFill(ctx, Math.round(stroke.points[0][0]), Math.round(stroke.points[0][1]), stroke.color, ctx.canvas.width, ctx.canvas.height);
		return;
	}

	const outline = getStrokeOutline(stroke);
	const pathData = getSvgPathFromStroke(outline);
	if (!pathData) return;

	const path = new Path2D(pathData);
	ctx.fillStyle = stroke.tool === 'eraser' ? '#ffffff' : stroke.color;
	ctx.fill(path);
}

function hexToRgba(hex: string): [number, number, number, number] {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return [r, g, b, 255];
}

function colorsMatch(data: Uint8ClampedArray, idx: number, target: [number, number, number, number], tolerance: number): boolean {
	return (
		Math.abs(data[idx] - target[0]) <= tolerance &&
		Math.abs(data[idx + 1] - target[1]) <= tolerance &&
		Math.abs(data[idx + 2] - target[2]) <= tolerance &&
		Math.abs(data[idx + 3] - target[3]) <= tolerance
	);
}

function floodFill(ctx: CanvasRenderingContext2D, startX: number, startY: number, fillHex: string, width: number, height: number): void {
	if (startX < 0 || startX >= width || startY < 0 || startY >= height) return;

	const imageData = ctx.getImageData(0, 0, width, height);
	const data = imageData.data;
	const fill = hexToRgba(fillHex);
	const tolerance = 32;

	const startIdx = (startY * width + startX) * 4;
	const target: [number, number, number, number] = [data[startIdx], data[startIdx + 1], data[startIdx + 2], data[startIdx + 3]];

	if (fill[0] === target[0] && fill[1] === target[1] && fill[2] === target[2] && fill[3] === target[3]) return;

	const stack: number[] = [startX, startY];
	const visited = new Uint8Array(width * height);

	while (stack.length >= 2) {
		const y = stack.pop() as number;
		const x = stack.pop() as number;

		if (x < 0 || x >= width || y < 0 || y >= height) continue;

		const key = y * width + x;
		if (visited[key]) continue;

		const idx = key * 4;
		if (!colorsMatch(data, idx, target, tolerance)) continue;

		visited[key] = 1;
		data[idx] = fill[0];
		data[idx + 1] = fill[1];
		data[idx + 2] = fill[2];
		data[idx + 3] = fill[3];

		stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
	}

	ctx.putImageData(imageData, 0, 0);
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) resolve(blob);
			else reject(new Error('Failed to export canvas'));
		}, 'image/png');
	});
}
